import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface JaEunProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function JaEun({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: JaEunProps) {
  if (!["BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["ja_eun", "jaeun"]) ? (
      <button
        type="button"
        className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["ja_eun", "jaeun"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">JaEun — Love &amp; Mercy</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="JaEun">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="pattern-details" className={triggerClass}>
              Pattern Details
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="training-focus" className={triggerClass}>
              Training Focus
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="mb-4 text-gray-700">
                JaEun, meaning &quot;Love &amp; Mercy&quot;, is a master-level pattern that embodies the principles of compassion and wisdom in
                martial arts. This pattern emphasizes the balance between power and control, demonstrating how true mastery includes both physical
                technique and spiritual development.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="pattern-details" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Details</h4>
              <ul className="list-disc space-y-1 pl-5 text-gray-700">
                <li>Number of Movements: 36</li>
                <li>Pattern Diagram: Cross (+)</li>
                <li>Starting Position: Ready Stance</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-disc space-y-1 pl-5 text-gray-700">
                <li>Flowing circular movements</li>
                <li>Controlled power application</li>
                <li>Harmonious combinations</li>
                <li>Precise technical execution</li>
                <li>Balanced defensive/offensive techniques</li>
                <li>Spiritual focus elements</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-disc space-y-1 pl-5 text-gray-700">
                <li>Technical mastery</li>
                <li>Mental cultivation</li>
                <li>Energy harmony</li>
                <li>Spiritual development</li>
                <li>Movement refinement</li>
                <li>Inner peace manifestation</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn}
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Information</h4>
          <p className="mb-4 text-gray-700">
            JaEun, meaning &quot;Love &amp; Mercy&quot;, is a master-level pattern that embodies the principles of compassion and wisdom in martial
            arts. This pattern emphasizes the balance between power and control, demonstrating how true mastery includes both physical technique and
            spiritual development.
          </p>
        </div>

        <div>
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Details</h4>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            <li>Number of Movements: 36</li>
            <li>Pattern Diagram: Cross (+)</li>
            <li>Starting Position: Ready Stance</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">Key Techniques</h4>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            <li>Flowing circular movements</li>
            <li>Controlled power application</li>
            <li>Harmonious combinations</li>
            <li>Precise technical execution</li>
            <li>Balanced defensive/offensive techniques</li>
            <li>Spiritual focus elements</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">Training Focus</h4>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            <li>Technical mastery</li>
            <li>Mental cultivation</li>
            <li>Energy harmony</li>
            <li>Spiritual development</li>
            <li>Movement refinement</li>
            <li>Inner peace manifestation</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
